import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { RegistrationFormData } from "@/utils/registrationSchema";

interface FormFieldsProps {
  formData: RegistrationFormData;
  onInputChange: (field: string, value: string) => void;
}

// Known course values that should use dropdown
const KNOWN_COURSES = ['web-programming', 'aicourse', 'cybersecurity', 'kids-programming', 'ai-teacher-training', ''];

export const FormFields = ({ formData, onInputChange }: FormFieldsProps) => {
  console.log("🔍 FormFields simplified render:", { formData });

  const handleInputChange = (field: string, value: string) => {
    console.log(`📝 Field "${field}" changed to: "${value}"`);
    onInputChange(field, value);
  };

  // Check if course is a custom faculty course (not in known list)
  const isCustomCourse = formData.course && !KNOWN_COURSES.includes(formData.course);

  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName" className="font-medium text-foreground">
            {"\u0531\u0576\u0578\u0582\u0576"} *
          </Label>
          <Input
            id="firstName"
            value={formData.firstName}
            onChange={(e) => handleInputChange("firstName", e.target.value)}
            className="border-input bg-background text-foreground focus:border-primary"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="lastName" className="font-medium text-foreground">
            {"\u0531\u0566\u0563\u0561\u0576\u0578\u0582\u0576"} *
          </Label>
          <Input
            id="lastName"
            value={formData.lastName}
            onChange={(e) => handleInputChange("lastName", e.target.value)}
            className="border-input bg-background text-foreground focus:border-primary"
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="phone" className="font-medium text-foreground">
          {"\u0540\u0565\u057C\u0561\u056D\u0578\u057D\u0561\u0570\u0561\u0574\u0561\u0580"} *
        </Label>
        <Input
          id="phone"
          type="tel"
          value={formData.phone}
          onChange={(e) => handleInputChange("phone", e.target.value)}
          className="border-input bg-background text-foreground focus:border-primary"
          placeholder="+374 XX XXX XXX"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email" className="font-medium text-foreground">
          {"\u0537\u056C. \u0583\u0578\u057D\u057F"} *
        </Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange("email", e.target.value)}
          className="border-input bg-background text-foreground focus:border-primary"
          placeholder="example@mail.com"
          required
        />
      </div>

      {/* Faculty field - show if prefilled */}
      {formData.faculty && (
        <div className="space-y-2">
          <Label htmlFor="faculty" className="font-medium text-foreground">
            {"\u0556\u0561\u056F\u0578\u0582\u056C\u057F\u0565\u057F"}
          </Label>
          <Input
            id="faculty"
            value={formData.faculty}
            className="border-input bg-muted text-foreground focus:border-primary"
            readOnly
          />
        </div>
      )}

      {/* Course field - show as readonly if custom faculty course, otherwise show dropdown */}
      <div className="space-y-2">
        <Label htmlFor="course" className="font-medium text-foreground">
          {"\u0534\u0561\u057D\u0568\u0576\u0569\u0561\u0581"} *
        </Label>
        {isCustomCourse ? (
          <Input
            id="course"
            value={formData.course}
            className="border-input bg-muted text-foreground focus:border-primary"
            readOnly
          />
        ) : (
          <Select value={formData.course} onValueChange={(value) => handleInputChange("course", value)}>
            <SelectTrigger className="border-input bg-background text-foreground focus:border-primary">
              <SelectValue placeholder={"\u0538\u0576\u057F\u0580\u0565\u0584 \u0564\u0561\u057D\u0568\u0576\u0569\u0561\u0581\u0568"} />
            </SelectTrigger>
            <SelectContent className="border-border bg-popover text-popover-foreground">
              <SelectItem value="web-programming">{"\u054E\u0565\u0562 \u056E\u0580\u0561\u0563\u0580\u0561\u057E\u0578\u0580\u0578\u0582\u0574"}</SelectItem>
              <SelectItem value="aicourse">{"\u0531\u0580\u0570\u0565\u057D\u057F\u0561\u056F\u0561\u0576 \u0562\u0561\u0576\u0561\u056F\u0561\u0576\u0578\u0582\u0569\u0575\u0578\u0582\u0576"}</SelectItem>
              <SelectItem value="cybersecurity">{"\u053F\u056B\u0562\u0565\u057C\u0561\u0576\u057E\u057F\u0561\u0576\u0563\u0578\u0582\u0569\u0575\u0578\u0582\u0576"}</SelectItem>
              <SelectItem value="kids-programming">{"\u053E\u0580\u0561\u0563\u0580\u0561\u057E\u0578\u0580\u0578\u0582\u0574 \u0565\u0580\u0565\u056D\u0561\u0576\u0565\u0580\u056B \u0570\u0561\u0574\u0561\u0580 (15+)"}</SelectItem>
              <SelectItem value="ai-teacher-training">{"AI-\u056B \u056F\u056B\u0580\u0561\u057C\u0578\u0582\u0574\u0568 \u0584\u0561\u0575\u056C \u0561\u057C \u0584\u0561\u0575\u056C (3 \u0585\u0580)"}</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Conditional field for Web Programming */}
      {formData.course === "web-programming" && (
        <div className="space-y-2">
          <Label htmlFor="specialization" className="font-medium text-foreground">
            {"\u0544\u0561\u057D\u0576\u0561\u0563\u056B\u057F\u0561\u0581\u0578\u0582\u0574"} *
          </Label>
          <Select value={formData.specialization} onValueChange={(value) => handleInputChange("specialization", value)}>
            <SelectTrigger className="border-input bg-background text-foreground focus:border-primary">
              <SelectValue placeholder={"\u0538\u0576\u057F\u0580\u0565\u0584 \u0574\u0561\u057D\u0576\u0561\u0563\u056B\u057F\u0561\u0581\u0578\u0582\u0574\u0568"} />
            </SelectTrigger>
            <SelectContent className="border-border bg-popover text-popover-foreground">
              <SelectItem value="frontend">Front-end</SelectItem>
              <SelectItem value="backend">Back-end</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Conditional field for Backend language selection */}
      {((formData.course === "web-programming" && formData.specialization === "backend") ||
        formData.course === "programming-languages") && (
        <div className="space-y-2">
          <Label htmlFor="language" className="font-medium text-foreground">
            {"\u053E\u0580\u0561\u0563\u0580\u0561\u057E\u0578\u0580\u0574\u0561\u0576 \u056C\u0565\u0566\u0578\u0582"} *
          </Label>
          <Select value={formData.language} onValueChange={(value) => handleInputChange("language", value)}>
            <SelectTrigger className="border-input bg-background text-foreground focus:border-primary">
              <SelectValue placeholder={"\u0538\u0576\u057F\u0580\u0565\u0584 \u056C\u0565\u0566\u0578\u0582\u0576"} />
            </SelectTrigger>
            <SelectContent className="border-border bg-popover text-popover-foreground">
              {formData.course === "web-programming" && formData.specialization === "backend" ? (
                <>
                  <SelectItem value="nodejs">Node.js</SelectItem>
                  <SelectItem value="csharp">C#</SelectItem>
                  <SelectItem value="python">Python</SelectItem>
                  <SelectItem value="java">Java</SelectItem>
                </>
              ) : (
                <>
                  <SelectItem value="cpp">C++</SelectItem>
                  <SelectItem value="csharp">C#</SelectItem>
                  <SelectItem value="java">Java</SelectItem>
                  <SelectItem value="python">Python</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="duration" className="font-medium text-foreground">
          {"\u054F\u0587\u0578\u0572\u0578\u0582\u0569\u0575\u0578\u0582\u0576"} *
        </Label>
        {formData.course === "ai-teacher-training" ? (
          <Input
            id="duration"
            value={"3 \u0585\u0580 (6 \u056A\u0561\u0574)"}
            className="border-input bg-muted text-foreground focus:border-primary"
            readOnly
          />
        ) : (
          <Select value={formData.duration || ""} onValueChange={(value) => handleInputChange("duration", value)}>
            <SelectTrigger className="border-input bg-background text-foreground focus:border-primary">
              <SelectValue placeholder={"\u0538\u0576\u057F\u0580\u0565\u0584 \u057F\u0587\u0578\u0572\u0578\u0582\u0569\u0575\u0578\u0582\u0576\u0568"} />
            </SelectTrigger>
            <SelectContent className="border-border bg-popover text-popover-foreground">
              <SelectItem value="6">{"\u0532\u0561\u0566\u0561\u0575\u056B\u0576 6 \u0561\u0574\u056B\u057D"}</SelectItem>
              <SelectItem value="9">{"\u0546\u0561\u056D\u0561\u0566\u0578\u0580\u0561\u056F\u0578\u0579\u0561\u0575\u056B\u0576 9 \u0561\u0574\u056B\u057D"}</SelectItem>
              <SelectItem value="18">{"\u0531\u056F\u0561\u0564\u0565\u0574\u056B\u0561\u056F\u0561\u0576 18 \u0561\u0574\u056B\u057D"}</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="space-y-3">
        <Label className="font-medium text-foreground">{"\u0548\u0582\u057D\u0578\u0582\u0581\u0574\u0561\u0576 \u057F\u0565\u057D\u0561\u056F"} *</Label>
        {formData.course === "ai-teacher-training" ? (
          <Input
            value={"\u053D\u0574\u0562\u0561\u0575\u056B\u0576"}
            className="border-input bg-muted text-foreground focus:border-primary"
            readOnly
          />
        ) : (
          <RadioGroup
            value={formData.learningType || ""}
            onValueChange={(value) => handleInputChange("learningType", value)}
            className="flex gap-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="individual" id="individual" />
              <Label htmlFor="individual" className="cursor-pointer text-foreground">
                {"\u0531\u0576\u0570\u0561\u057F\u0561\u056F\u0561\u0576"}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="group" id="group" />
              <Label htmlFor="group" className="cursor-pointer text-foreground">
                {"\u053D\u0574\u0562\u0561\u0575\u056B\u0576"}
              </Label>
            </div>
          </RadioGroup>
        )}
      </div>

      <div className="space-y-3">
        <Label className="font-medium text-foreground">{"\u0548\u0582\u057D\u0578\u0582\u0581\u0574\u0561\u0576 \u0571\u0587"} *</Label>
        {formData.course === "ai-teacher-training" ? (
          <Input
            value={"\u0531\u057C\u056F\u0561"}
            className="border-input bg-muted text-foreground focus:border-primary"
            readOnly
          />
        ) : (
          <RadioGroup
            value={formData.learningFormat || ""}
            onValueChange={(value) => handleInputChange("learningFormat", value)}
            className="flex gap-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="online" id="online" />
              <Label htmlFor="online" className="cursor-pointer text-foreground">
                {"\u0555\u0576\u056C\u0561\u0575\u0576"}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="inPerson" id="inPerson" />
              <Label htmlFor="inPerson" className="cursor-pointer text-foreground">
                {"\u0531\u057C\u056F\u0561"}
              </Label>
            </div>
          </RadioGroup>
        )}
      </div>
    </>
  );
};